import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const services = [
  { title: 'Warehousing & Storage', description: 'Secure warehousing solutions in Nairobi, Dar es Salaam, and Mombasa with inventory management systems, climate-controlled storage, and 24/7 security monitoring.', icon: 'ri-archive-line' },
  { title: 'Inventory Optimization', description: 'Data-driven inventory planning to minimize holding costs while ensuring stock availability — demand forecasting, safety stock calculation, and replenishment automation.', icon: 'ri-bar-chart-box-line' },
  { title: 'Multi-Modal Transportation', description: 'Integrated road, rail, sea, and air freight solutions optimized for cost, speed, and reliability — with real-time tracking across all transport modes.', icon: 'ri-truck-line' },
  { title: 'Last-Mile Delivery', description: 'Reliable final-leg distribution across East Africa\'s cities, towns, and remote locations — ensuring your products reach their destination on time and intact.', icon: 'ri-map-pin-2-line' },
  { title: 'Supply Chain Visibility', description: 'End-to-end shipment tracking and supply chain analytics providing complete transparency — from factory gate to final delivery point.', icon: 'ri-radar-line' },
  { title: 'Cold Chain Logistics', description: 'Temperature-controlled storage and transportation for perishable goods, pharmaceuticals, and sensitive products requiring strict environmental monitoring.', icon: 'ri-temp-cold-line' },
];

const stats = [
  { number: '50,000+', label: 'Sq. Ft. Warehouse Space' },
  { number: '15+', label: 'Transport Partners' },
  { number: '99.2%', label: 'On-Time Delivery Rate' },
  { number: '24/7', label: 'Operations & Monitoring' },
];

export default function LogisticsPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">

        {/* Hero */}
        <section className="relative w-full bg-primary-900 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20large%20warehouse%20logistics%20center%20interior%20with%20organized%20pallet%20racks%2C%20forklifts%20moving%20goods%2C%20efficient%20supply%20chain%20operations%2C%20clean%20industrial%20aesthetic%2C%20soft%20overhead%20lighting%2C%20professional%20corporate%20photography&width=1920&height=800&seq=logistics-hero&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/90 via-primary-950/80 to-primary-950/95"></div>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-16 md:py-24">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">Services</span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-5 leading-tight">
              Logistics
            </h1>
            <p className="text-white/50 max-w-2xl text-sm md:text-base leading-relaxed mb-8">
              Comprehensive logistics management including warehousing, inventory optimization, multi-modal transportation, and last-mile delivery — keeping your supply chain moving efficiently across East Africa.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
              Get Logistics Support <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* Overview */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Overview</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">End-to-End Supply Chain Management</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Logistics in East Africa presents unique challenges — from infrastructure gaps to cross-border complexities. BEKAALI&apos;s logistics division turns these challenges into competitive advantages through strategic planning, local expertise, and integrated operations.
              </p>
            </div>

            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                A reliable, efficient logistics operation is the backbone of successful international trade. Yet many businesses in East Africa struggle with fragmented logistics providers, unpredictable delivery times, inventory management challenges, and limited supply chain visibility. BEKAALI&apos;s integrated logistics division addresses these pain points comprehensively.
              </p>
              <p>
                We operate dedicated warehousing facilities in Nairobi and Dar es Salaam, with strategic partnerships for storage capacity across Mombasa, Kampala, and Kigali. Our inventory management systems provide real-time visibility into stock levels, movement patterns, and replenishment requirements — enabling data-driven decision making that minimizes costs while ensuring product availability.
              </p>
              <p>
                Our multi-modal transportation network combines road, rail, sea, and air options to deliver the optimal balance of speed and cost for every shipment. With real-time GPS tracking, proactive exception management, and experienced local teams at every transit point, we ensure your cargo moves reliably from origin to destination.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="text-center p-6 bg-background-50 border border-background-200/70 rounded-2xl">
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">{stat.number}</div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Services Grid */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Capabilities</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Logistics Services</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {services.map((svc, i) => (
                <div key={i} className="group bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-accent-200/50 transition-all duration-400">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4 group-hover:bg-accent-500 group-hover:text-white transition-all duration-400">
                    <i className={`${svc.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{svc.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{svc.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Optimize Your Supply Chain</h2>
            <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base leading-relaxed mb-8">
              Let&apos;s design a logistics solution tailored to your business — from warehousing to last-mile delivery.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
              Contact Our Logistics Team <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}